/**
 * The share-session context an embedded app's consent surfaces need, decoded from the
 * share session token (display-only — the server re-verifies it on every call). ONE
 * decode for the share viewer's PDApp run view and the HTML editor's share branch.
 *
 * The consent identity is the session's STORED acting pair (`at` / `ae`) — the key the
 * server reads and writes consent under — never the link's verified email: after an
 * identity switch the two differ, and naming the verified one 403s `identity_not_owned`
 * (an endless 412 loop) or writes a row the runtime never reads. No stored pair → null.
 */
/**
 * #796 — the window event an embedded app's host dispatches when the server answers
 * `reentry_required` (the app REQUIRES identity and this share visit has none): the share
 * viewer's chrome answers it with its OWN sign-in (the quick-view "Sign in" → identity
 * picker), so there is exactly one sign-in flow. The chrome listens for the same literal
 * (`ShareViewerChrome.tsx`; pinned equal by a test).
 */
export const SHARE_SIGN_IN_EVENT = 'papan:share-sign-in-required'

export interface ShareSessionContext {
  shareId: string | null
  shareToken: string | null
  actingTenantId: string | null
  actingEmail: string | null
  /** The email VERIFIED for the link — for a Personal identity, the email it CHOSE for this share
   *  (#794): what the identity bar displays as "you" (never a permission key). */
  verifiedEmail: string | null
}

export function shareSessionContext(sessionToken: string | null | undefined): ShareSessionContext | null {
  if (!sessionToken) return null
  try {
    const b64 = sessionToken.split('.')[0]!.replace(/-/g, '+').replace(/_/g, '/')
    const p = JSON.parse(atob(b64 + '==='.slice((b64.length + 3) % 4))) as Record<string, unknown>
    // A v2 token (#791) seals its payload: nothing to read here — callers pass the context
    // from the session's /info instead (ShareViewerChrome's shareContextOf).
    if (p.v === 2) return null
    const at = typeof p.at === 'string' ? p.at : null
    return {
      shareId: typeof p.shareId === 'string' ? p.shareId : null,
      shareToken: typeof p.shareToken === 'string' ? p.shareToken : null,
      actingTenantId: at,
      actingEmail: at && typeof p.ae === 'string' ? p.ae : null,
      verifiedEmail: typeof p.verifiedEmail === 'string' ? p.verifiedEmail.toLowerCase() : null,
    }
  } catch {
    return null
  }
}
